import React, { useEffect, useState } from 'react';
import { 
  Modal, 
  Form, 
  Input, 
  DatePicker, 
  InputNumber,
  Switch,
  message,
  Alert,
  Divider,
  Button,
  Space,
  Select,
  Table,
  Popconfirm,
  Tag
} from 'antd';
import { 
  BookOutlined, 
  CalendarOutlined,
  DollarOutlined,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  UserOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { CourseList, CourseCreate, CourseSubjectCreate, courseAPI } from '../../services/courseAPI';
import { specialtyAPI, SpecialtySelect } from '../../services/specialtyAPI';
import { teacherAPI, TeacherList } from '../../services/teacherAPI';
import { studentAPI, Student } from '../../services/studentAPI';
import { UserCompany, Company } from '../../services/companyAPI';
import dayjs from 'dayjs';

const { TextArea } = Input;
const { RangePicker } = DatePicker;

// Union tip yaradırıq
type SelectedCompany = UserCompany | Company;

interface CourseFormProps {
  visible: boolean;
  course: CourseList | null;
  onCancel: () => void;
  onSuccess: () => void;
  selectedCompany: SelectedCompany | null;
}

interface SubjectFormData {
  specialty: number;
  teacher: number;
  hours: number;
  order: number;
}

interface StudentFormData {
  id?: number; // CourseStudent record ID-si
  student: number;
  start_date?: string;
  end_date?: string;
  payment_amount?: number;
  payment_date?: string;
  notes?: string;
}

const CourseForm: React.FC<CourseFormProps> = ({ 
  visible, 
  course, 
  onCancel, 
  onSuccess, 
  selectedCompany 
}) => {
  const [form] = Form.useForm();
  const [subjectForm] = Form.useForm();
  const [studentForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [specialtyOptions, setSpecialtyOptions] = useState<SpecialtySelect[]>([]);
  const [teacherOptions, setTeacherOptions] = useState<TeacherList[]>([]);
  const [studentOptions, setStudentOptions] = useState<Student[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [subjects, setSubjects] = useState<SubjectFormData[]>([]);
  const [students, setStudents] = useState<StudentFormData[]>([]);
  const [isAddingSubject, setIsAddingSubject] = useState(false);
  const [isAddingStudent, setIsAddingStudent] = useState(false);
  const [editingStudent, setEditingStudent] = useState<StudentFormData | null>(null);
  const [isEditingStudent, setIsEditingStudent] = useState(false);

  useEffect(() => {
    if (visible && selectedCompany) {
      loadOptions();
      
      if (course) {
        // Redaktə rejimi - mövcud kurs məlumatlarını yüklə
        loadCourseDetails();
      } else {
        // Yeni kurs əlavə etmə rejimi
        form.resetFields();
        form.setFieldsValue({ 
          company: selectedCompany.id,
          is_active: true
        });
        setSubjects([]);
        setStudents([]);
      }
    }
  }, [visible, course, selectedCompany]);

  const loadOptions = async () => {
    if (!selectedCompany) return;
    
    setLoadingOptions(true);
    try {
      // İxtisasları yüklə
      const specialtyResponse = await specialtyAPI.getSpecialtySelectOptions(selectedCompany.id);
      if (specialtyResponse.success && specialtyResponse.data) {
        setSpecialtyOptions(specialtyResponse.data);
      }

      // Müəllimləri yüklə
      const teacherResponse = await teacherAPI.getTeachers({ company: selectedCompany.id });
      if (teacherResponse.success && teacherResponse.data) {
        setTeacherOptions(teacherResponse.data);
      }

      // Tələbələri yüklə
      const studentResponse = await studentAPI.getStudents({ company: selectedCompany.id });
      if (studentResponse.success && studentResponse.data) {
        setStudentOptions(studentResponse.data);
      }
    } catch (error) {
      console.error('Seçimlər yüklənmədi:', error);
      message.error('Seçimlər yüklənmədi');
    } finally {
      setLoadingOptions(false);
    }
  };

  const loadCourseDetails = async () => {
    if (!course) return;
    
    try {
      const response = await courseAPI.getCourse(course.id);
      
      if (response.success && response.data) {
        const courseData = response.data;
        
        form.setFieldsValue({
          name: courseData.name,
          description: courseData.description,
          start_date: courseData.start_date ? dayjs(courseData.start_date) : null,
          end_date: courseData.end_date ? dayjs(courseData.end_date) : null,
          price: courseData.price,
          is_active: courseData.is_active,
          company: courseData.company
        });

        // Fənnləri yüklə
        const subjectsData = (courseData.subjects || []).map((subject, index) => ({
          specialty: subject.specialty,
          teacher: subject.teacher,
          hours: subject.hours,
          order: subject.order || index + 1
        }));
        setSubjects(subjectsData);

        // Tələbələri yüklə (əgər course_students sahəsi varsa)
        const studentsData = (courseData.course_students || []).map(courseStudent => ({
          id: courseStudent.id, // CourseStudent record ID-si
          student: courseStudent.student,
          start_date: courseStudent.start_date || undefined,
          end_date: courseStudent.end_date || undefined,
          payment_amount: courseStudent.payment_amount || undefined,
          payment_date: courseStudent.payment_date || undefined,
          notes: courseStudent.notes || ''
        }));
        
        console.log('loadCourseDetails - courseData.course_students:', courseData.course_students);
        console.log('loadCourseDetails - studentsData:', studentsData);
        
        setStudents(studentsData);
      } else {
        message.error(response.message || 'Kurs məlumatları yüklənmədi');
      }
    } catch (error) {
      console.error('Kurs məlumatları yüklənmədi:', error);
      message.error('Kurs məlumatları yüklənmədi');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const courseData: CourseCreate = {
        name: values.name,
        company: values.company,
        description: values.description || '',
        start_date: values.start_date ? values.start_date.format('YYYY-MM-DD') : null,
        end_date: values.end_date ? values.end_date.format('YYYY-MM-DD') : null,
        price: values.price || null,
        is_active: values.is_active,
        subjects: subjects,
        students: students.map(s => ({
          course: 0, // Bu backend-də set ediləcək
          student: s.student,
          start_date: s.start_date || null,
          end_date: s.end_date || null,
          payment_amount: s.payment_amount || null,
          payment_date: s.payment_date || null,
          is_active: true,
          notes: s.notes || ''
        }))
      };

      let response;
      if (course) {
        response = await courseAPI.updateCourse(course.id, courseData);
      } else {
        response = await courseAPI.createCourse(courseData);
      }

      if (response.success) {
        message.success(response.message || (course ? 'Kurs yeniləndi' : 'Kurs əlavə edildi'));
        resetInternalState();
        onSuccess();
      } else {
        message.error(response.message || 'Əməliyyat uğursuz oldu');
      }
    } catch (error) {
      console.error('Form xətası:', error);
      message.error('Form məlumatlarını yoxlayın');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSubject = () => {
    setIsAddingSubject(true);
  };

  const handleSubjectSubmit = (values: any) => {
    const selectedSpecialties = Array.isArray(values.specialty) ? values.specialty : [values.specialty];
    
    // Hər seçilmiş ixtisas üçün ayrı fənn yaradırıq
    const newSubjects: SubjectFormData[] = selectedSpecialties.map((specialtyId: number, index: number) => ({
      specialty: specialtyId,
      teacher: values.teacher,
      hours: values.hours || 0,
      order: Array.isArray(subjects) ? subjects.length + index + 1 : index + 1
    }));

    // Artıq mövcud olan ixtisasları yoxla
    const existingSpecialties = Array.isArray(subjects) ? subjects.map(s => s.specialty) : [];
    const duplicateSpecialties = newSubjects.filter(ns => existingSpecialties.includes(ns.specialty));
    
    if (duplicateSpecialties.length > 0) {
      const duplicateNames = duplicateSpecialties.map(ds => getSpecialtyName(ds.specialty)).join(', ');
      message.warning(`Bu ixtisas(lar) artıq əlavə edilib: ${duplicateNames}`);
      return;
    }

    // Yeni fənnləri əlavə et
    setSubjects(Array.isArray(subjects) ? [...subjects, ...newSubjects] : newSubjects);
    setIsAddingSubject(false);
    
    const addedCount = newSubjects.length;
    message.success(`${addedCount} fənn əlavə edildi`);
  };

  const handleAddStudent = () => {
    setIsAddingStudent(true);
  };

  const handleStudentSubmit = async (values: any) => {
    try {
      const newStudent: StudentFormData = {
        student: values.student,
        start_date: values.start_date ? values.start_date.format('YYYY-MM-DD') : undefined,
        end_date: values.end_date ? values.end_date.format('YYYY-MM-DD') : undefined,
        payment_amount: values.payment_amount || undefined,
        payment_date: values.payment_date ? values.payment_date.format('YYYY-MM-DD') : undefined,
        notes: values.notes || ''
      };

      // Eyni tələbənin əlavə edilməsinin qarşısını al
      if (Array.isArray(students) && students.some(s => s.student === newStudent.student)) {
        message.warning('Bu tələbə artıq əlavə edilib');
        return;
      }

      // Əgər kurs redaktə rejimindədirsə, dərhal backend-ə əlavə et
      if (course) {
        const courseStudentData = {
          course: course.id,
          student: newStudent.student,
          start_date: newStudent.start_date || null,
          end_date: newStudent.end_date || null,
          payment_amount: newStudent.payment_amount || null,
          payment_date: newStudent.payment_date || null,
          notes: newStudent.notes || '',
          is_active: true
        };

        const response = await courseAPI.addStudentToCourse(courseStudentData);
        
        if (response.success) {
          // Local state-i də yenilə
          setStudents(Array.isArray(students) ? [...students, newStudent] : [newStudent]);
          setIsAddingStudent(false);
          message.success('Tələbə kursa əlavə edildi');
        } else {
          message.error(response.message || 'Tələbə əlavə edilmədi');
        }
      } else {
        // Yeni kurs yaradılarkən local state-də saxla
        setStudents(Array.isArray(students) ? [...students, newStudent] : [newStudent]);
        setIsAddingStudent(false);
        message.success('Tələbə əlavə edildi');
      }
    } catch (error) {
      console.error('Tələbə əlavə etmə xətası:', error);
      message.error('Tələbə əlavə edilmədi');
    }
  };

  const handleEditStudent = (student: StudentFormData) => {
    setEditingStudent(student);
    setIsEditingStudent(true);
    
    // Form-u doldur - setTimeout ilə form-un render olmasını gözlə
    setTimeout(() => {
      const formValues = {
        student: student.student,
        start_date: student.start_date ? dayjs(student.start_date) : null,
        end_date: student.end_date ? dayjs(student.end_date) : null,
        payment_amount: student.payment_amount || null,
        payment_date: student.payment_date ? dayjs(student.payment_date) : null,
        notes: student.notes || ''
      };
      
      console.log('Tələbə redaktə məlumatları:', formValues);
      studentForm.setFieldsValue(formValues);
    }, 100);
  };

  const handleUpdateStudent = async (values: any) => {
    if (!editingStudent) return;
    
    console.log('handleUpdateStudent - editingStudent:', editingStudent);
    console.log('handleUpdateStudent - values:', values);
    
    try {
      const updatedStudentData = {
        student: values.student,
        start_date: values.start_date ? values.start_date.format('YYYY-MM-DD') : null,
        end_date: values.end_date ? values.end_date.format('YYYY-MM-DD') : null,
        payment_amount: values.payment_amount || null,
        payment_date: values.payment_date ? values.payment_date.format('YYYY-MM-DD') : null,
        notes: values.notes || '',
        is_active: true
      };

      console.log('handleUpdateStudent - updatedStudentData:', updatedStudentData);

      // Əgər kurs redaktə rejimindədirsə və CourseStudent ID-si varsa, backend-ə yenilə
      if (course && editingStudent.id) {
        // course sahəsini də əlavə et
        const updatedStudentDataWithCourse = {
          ...updatedStudentData,
          course: course.id
        };
        
        console.log('handleUpdateStudent - API çağırışı:', editingStudent.id, updatedStudentDataWithCourse);
        const response = await courseAPI.updateCourseStudent(editingStudent.id, updatedStudentDataWithCourse);
        
        console.log('handleUpdateStudent - API cavabı:', response);
        
        if (response.success) {
          // Local state-i də yenilə
          const updatedStudents = students.map(s => 
            s.id === editingStudent.id 
              ? { ...s, ...updatedStudentData, student: values.student }
              : s
          );
          console.log('handleUpdateStudent - yenilənmiş tələbələr:', updatedStudents);
          setStudents(updatedStudents);
          setIsEditingStudent(false);
          setEditingStudent(null);
          message.success('Tələbə məlumatları yeniləndi');
        } else {
          console.error('handleUpdateStudent - API xətası:', response.message);
          message.error(response.message || 'Tələbə məlumatları yenilənmədi');
        }
      } else {
        console.log('handleUpdateStudent - yalnız local state yenilənir');
        // Yeni kurs yaradılarkən yalnız local state-i yenilə
        const updatedStudents = students.map(s => 
          s.student === editingStudent.student 
            ? { ...s, ...updatedStudentData, student: values.student }
            : s
        );
        setStudents(updatedStudents);
        setIsEditingStudent(false);
        setEditingStudent(null);
        message.success('Tələbə məlumatları yeniləndi');
      }
    } catch (error) {
      console.error('Tələbə yeniləmə xətası:', error);
      message.error('Tələbə məlumatları yenilənmədi');
    }
  };

  const handleCancelEditStudent = () => {
    setIsEditingStudent(false);
    setEditingStudent(null);
    studentForm.resetFields();
  };

  const handleRemoveStudent = async (index: number) => {
    try {
      if (!Array.isArray(students)) return;
      
      const studentToRemove = students[index];
      
      // Əgər kurs redaktə rejimindədirsə və tələbə backend-də varsa, onu sil
      if (course && studentToRemove) {
        // Backend-dən tələbəni sil
        const response = await courseAPI.removeStudentFromCourse(course.id, studentToRemove.student);
        if (response.success) {
          // Local state-dən də sil
          const newStudents = students.filter((_, i) => i !== index);
          setStudents(newStudents);
          message.success('Tələbə kursdan çıxarıldı');
        } else {
          message.error(response.message || 'Tələbə silinmədi');
        }
      } else {
        // Yeni kurs yaradılarkən yalnız local state-dən sil
        const newStudents = students.filter((_, i) => i !== index);
        setStudents(newStudents);
        message.success('Tələbə silindi');
      }
    } catch (error) {
      console.error('Tələbə silmə xətası:', error);
      message.error('Tələbə silinmədi');
    }
  };

  const handleRemoveSubject = (index: number) => {
    if (!Array.isArray(subjects)) return;
    const newSubjects = subjects.filter((_, i) => i !== index);
    setSubjects(newSubjects);
  };

  const resetInternalState = () => {
    setSubjects([]);
    setStudents([]);
    setIsAddingSubject(false);
    setIsAddingStudent(false);
    setIsEditingStudent(false);
    setEditingStudent(null);
  };

  const handleCancel = () => {
    resetInternalState();
    onCancel();
  };

  const getSpecialtyName = (specialtyId: number) => {
    if (!Array.isArray(specialtyOptions)) return 'Naməlum';
    const specialty = specialtyOptions.find(s => s.value === specialtyId);
    return specialty ? specialty.label : 'Naməlum';
  };

  const getTeacherName = (teacherId: number) => {
    if (!Array.isArray(teacherOptions)) return 'Naməlum';
    const teacher = teacherOptions.find(t => t.id === teacherId);
    return teacher ? teacher.full_name : 'Naməlum';
  };

  const getStudentName = (studentId: number) => {
    if (!Array.isArray(studentOptions)) return 'Naməlum';
    const student = studentOptions.find(s => s.id === studentId);
    return student ? student.full_name || `${student.first_name} ${student.last_name}` : 'Naməlum';
  };

  const studentColumns: ColumnsType<StudentFormData> = [
    {
      title: 'Tələbə',
      dataIndex: 'student',
      key: 'student',
      render: (studentId: number) => (
        <div className="flex items-center">
          <UserOutlined className="mr-1 text-green-500" />
          {getStudentName(studentId)}
        </div>
      ),
    },
    {
      title: 'Başlama tarixi',
      dataIndex: 'start_date',
      key: 'start_date',
      render: (date: string) => date ? new Date(date).toLocaleDateString('az-AZ') : '-',
    },
    {
      title: 'Bitmə tarixi',
      dataIndex: 'end_date',
      key: 'end_date',
      render: (date: string) => date ? new Date(date).toLocaleDateString('az-AZ') : '-',
    },
    {
      title: 'Ödəniş',
      dataIndex: 'payment_amount',
      key: 'payment_amount',
      render: (amount: number) => amount ? `${amount} AZN` : '-',
    },
    {
      title: 'Əməliyyat',
      key: 'action',
      render: (_, record, index) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditStudent(record)}
            title="Redaktə et"
          />
          <Popconfirm
            title="Tələbəni silmək"
            description="Bu tələbəni kursdan silmək istədiyinizə əminsiniz?"
            onConfirm={() => handleRemoveStudent(index)}
            okText="Bəli"
            cancelText="Xeyr"
            okType="danger"
          >
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              title="Sil"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const subjectColumns: ColumnsType<SubjectFormData> = [
    {
      title: 'Fənn (İxtisas)',
      dataIndex: 'specialty',
      key: 'specialty',
      render: (specialtyId: number) => getSpecialtyName(specialtyId),
    },
    {
      title: 'Müəllim',
      dataIndex: 'teacher',
      key: 'teacher',
      render: (teacherId: number) => (
        <div className="flex items-center">
          <UserOutlined className="mr-1 text-blue-500" />
          {getTeacherName(teacherId)}
        </div>
      ),
    },
    {
      title: 'Saat',
      dataIndex: 'hours',
      key: 'hours',
      render: (hours: number) => `${hours} saat`,
    },
    {
      title: 'Əməliyyat',
      key: 'action',
      render: (_, record, index) => (
        <Popconfirm
          title="Fənni silmək"
          description="Bu fənni kursdan silmək istədiyinizə əminsiniz?"
          onConfirm={() => handleRemoveSubject(index)}
          okText="Bəli"
          cancelText="Xeyr"
          okType="danger"
        >
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
          />
        </Popconfirm>
      ),
    },
  ];

  return (
    <Modal
      title={
        <div className="flex items-center">
          <BookOutlined className="mr-2 text-blue-500" />
          {course ? 'Kursu Redaktə Et' : 'Yeni Kurs Əlavə Et'}
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      onOk={handleSubmit}
      confirmLoading={loading}
      width={800}
      okText={course ? 'Yenilə' : 'Əlavə Et'}
      cancelText="Ləğv Et"
      destroyOnClose={false}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
      >
        <Alert
          message="Kurs məlumatları"
          description="Kurs adı, təsviri və digər əsas məlumatları daxil edin. Sonra kursun fənnlərini əlavə edə bilərsiniz."
          type="info"
          showIcon
          className="mb-6"
        />

        {/* Əsas məlumatlar */}
        <Divider>
          <BookOutlined className="mr-2" />
          Kurs məlumatları
        </Divider>

        <Form.Item
          name="name"
          label="Kurs adı"
          rules={[
            { required: true, message: 'Kurs adı tələb olunur!' },
            { min: 2, message: 'Kurs adı ən azı 2 simvol olmalıdır!' },
            { max: 200, message: 'Kurs adı maksimum 200 simvol ola bilər!' }
          ]}
        >
          <Input 
            placeholder="Kursun adını daxil edin (məsələn: Magistr hazırlığı kursu)"
            prefix={<BookOutlined className="text-gray-400" />}
          />
        </Form.Item>

        <Form.Item
          name="description"
          label="Təsvir"
          help="Kurs haqqında qısa məlumat"
        >
          <TextArea
            rows={3}
            placeholder="Kursun təsvirini daxil edin..."
            maxLength={500}
            showCount
          />
        </Form.Item>

        {/* Tarix və qiymət */}
        <Divider>
          <CalendarOutlined className="mr-2" />
          Tarix və qiymət
        </Divider>

        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            name="start_date"
            label="Başlama tarixi"
          >
            <DatePicker
              style={{ width: '100%' }}
              placeholder="Başlama tarixi seçin"
              format="DD.MM.YYYY"
            />
          </Form.Item>

          <Form.Item
            name="end_date"
            label="Bitmə tarixi"
          >
            <DatePicker
              style={{ width: '100%' }}
              placeholder="Bitmə tarixi seçin"
              format="DD.MM.YYYY"
            />
          </Form.Item>
        </div>

        <Form.Item
          name="price"
          label="Qiymət (AZN)"
          help="Kursun qiyməti"
        >
          <InputNumber
            min={0}
            step={0.01}
            placeholder="0.00"
            style={{ width: '100%' }}
            prefix={<DollarOutlined className="text-gray-400" />}
          />
        </Form.Item>

        {/* Fənnlər */}
        <Divider>
          <BookOutlined className="mr-2" />
          Kurs fənnləri
        </Divider>

        <div className="mb-4">
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={handleAddSubject}
            className="w-full"
            disabled={isAddingSubject}
          >
            Fənn(lər) əlavə et
          </Button>
        </div>

        {isAddingSubject && (
          <div className="border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50">
            <div className="mb-3">
              <Alert
                message="Fənn əlavə etmə"
                description="Bir neçə ixtisas seçə bilərsiniz. Seçilmiş hər ixtisas üçün eyni müəllim və saat sayı tətbiq ediləcək."
                type="info"
                showIcon
                className="mb-4"
              />
            </div>
            <Form
              form={subjectForm}
              layout="vertical"
              onFinish={handleSubjectSubmit}
              size="small"
              preserve={false}
            >
              <div className="grid grid-cols-2 gap-4">
                <Form.Item
                  name="specialty"
                  label="Fənn (İxtisas)"
                  rules={[{ required: true, message: 'Ən azı bir fənn seçin!' }]}
                >
                  <Select
                    mode="multiple"
                    placeholder="Fənn(lər) seçin"
                    loading={loadingOptions}
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={Array.isArray(specialtyOptions) ? specialtyOptions
                      .filter(s => !Array.isArray(subjects) || !subjects.some(sub => sub.specialty === s.value))
                      .map(s => ({
                        value: s.value,
                        label: s.label
                      })) : []}
                  />
                </Form.Item>

                <Form.Item
                  name="teacher"
                  label="Müəllim"
                  rules={[{ required: true, message: 'Müəllim seçin!' }]}
                >
                  <Select
                    placeholder="Müəllim seçin"
                    loading={loadingOptions}
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={Array.isArray(teacherOptions) ? teacherOptions.map(t => ({
                      value: t.id,
                      label: t.full_name
                    })) : []}
                  />
                </Form.Item>
              </div>

              <Form.Item
                name="hours"
                label="Saat sayı"
                rules={[{ required: true, message: 'Saat sayı daxil edin!' }]}
              >
                <InputNumber
                  min={1}
                  placeholder="40"
                  style={{ width: '100%' }}
                />
              </Form.Item>

              <div className="flex justify-end space-x-2">
                <Button onClick={() => setIsAddingSubject(false)}>
                  Ləğv et
                </Button>
                <Button 
                  type="primary" 
                  onClick={async () => {
                    try {
                      const values = await subjectForm.validateFields();
                      handleSubjectSubmit(values);
                      subjectForm.resetFields();
                    } catch (error) {
                      console.log('Validation failed:', error);
                    }
                  }}
                >
                  Əlavə et
                </Button>
              </div>
            </Form>
          </div>
        )}

        {Array.isArray(subjects) && subjects.length > 0 && (
          <Table
            columns={subjectColumns}
            dataSource={subjects}
            rowKey={(record, index) => `${record.specialty}-${record.teacher}-${index}`}
            pagination={false}
            size="small"
            className="mb-4"
          />
        )}

        {/* Tələbələr */}
        <Divider>
          <UserOutlined className="mr-2" />
          Kurs tələbələri
        </Divider>

        <div className="mb-4">
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={handleAddStudent}
            className="w-full"
            disabled={isAddingStudent || isEditingStudent}
          >
            Tələbə əlavə et
          </Button>
        </div>

        {(isAddingStudent || isEditingStudent) && (
          <div className="border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50">
            <div className="mb-3">
              <Alert
                message={isEditingStudent ? "Tələbə məlumatlarını redaktə et" : "Tələbə əlavə etmə"}
                description={isEditingStudent ? "Tələbənin kurs məlumatlarını yeniləyin." : "Kursa yeni tələbə əlavə edin."}
                type="info"
                showIcon
                className="mb-4"
              />
            </div>
            <Form
              form={studentForm}
              layout="vertical"
              size="small"
              preserve={false}
            >
              <Form.Item
                name="student"
                label="Tələbə"
                rules={[{ required: true, message: 'Tələbə seçin!' }]}
              >
                <Select
                  placeholder="Tələbə seçin"
                  loading={loadingOptions}
                  showSearch
                  disabled={isEditingStudent} // Redaktə zamanı disabled et
                  onChange={(value) => {
                    // Bu onChange yalnız əlavə etmə zamanı işləyəcək
                    if (!isEditingStudent) {
                      // Normal əlavə etmə prosesi
                    }
                  }}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={Array.isArray(studentOptions) ? (() => {
                    if (isEditingStudent && editingStudent) {
                      // Redaktə zamanı: hazırki tələbəni və digər mövcud olmayan tələbələri göstər
                      return studentOptions
                        .filter(s => {
                          // Hazırki redaktə olunan tələbəni həmişə göstər
                          if (s.id === editingStudent.student) {
                            return true;
                          }
                          // Digər tələbələr üçün artıq əlavə edilmiş olanları gizlət
                          return !students.some(stud => stud.student === s.id);
                        })
                        .map(s => ({
                          value: s.id!,
                          label: `${s.full_name || `${s.first_name} ${s.last_name}`} (${s.fin_code})`
                        }));
                    } else {
                      // Əlavə etmə zamanı: artıq əlavə edilmiş tələbələri gizlət
                      return studentOptions
                        .filter(s => !students.some(stud => stud.student === s.id))
                        .map(s => ({
                          value: s.id!,
                          label: `${s.full_name || `${s.first_name} ${s.last_name}`} (${s.fin_code})`
                        }));
                    }
                  })() : []}
                />
              </Form.Item>

              <div className="grid grid-cols-2 gap-4">
                <Form.Item
                  name="start_date"
                  label="Başlama tarixi"
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    placeholder="Başlama tarixi seçin"
                    format="DD.MM.YYYY"
                  />
                </Form.Item>

                <Form.Item
                  name="end_date"
                  label="Bitmə tarixi"
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    placeholder="Bitmə tarixi seçin"
                    format="DD.MM.YYYY"
                  />
                </Form.Item>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Form.Item
                  name="payment_amount"
                  label="Ödəniş məbləği (AZN)"
                >
                  <InputNumber
                    min={0}
                    step={0.01}
                    placeholder="0.00"
                    style={{ width: '100%' }}
                    prefix={<DollarOutlined className="text-gray-400" />}
                  />
                </Form.Item>

                <Form.Item
                  name="payment_date"
                  label="Ödəniş tarixi"
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    placeholder="Ödəniş tarixi seçin"
                    format="DD.MM.YYYY"
                  />
                </Form.Item>
              </div>

              <Form.Item
                name="notes"
                label="Qeydlər"
              >
                <TextArea
                  rows={2}
                  placeholder="Tələbə haqqında qeydlər..."
                  maxLength={200}
                />
              </Form.Item>

              <div className="flex justify-end space-x-2">
                <Button onClick={isEditingStudent ? handleCancelEditStudent : () => setIsAddingStudent(false)}>
                  Ləğv et
                </Button>
                <Button 
                  type="primary" 
                  onClick={async () => {
                    try {
                      const values = await studentForm.validateFields();
                      if (isEditingStudent) {
                        await handleUpdateStudent(values);
                      } else {
                        await handleStudentSubmit(values);
                      }
                      studentForm.resetFields();
                    } catch (error) {
                      console.log('Validation failed:', error);
                    }
                  }}
                >
                  {isEditingStudent ? 'Yenilə' : 'Əlavə et'}
                </Button>
              </div>
            </Form>
          </div>
        )}

        {Array.isArray(students) && students.length > 0 && (
          <Table
            columns={studentColumns}
            dataSource={students}
            rowKey={(record, index) => `${record.student}-${index}`}
            pagination={false}
            size="small"
            className="mb-4"
          />
        )}

        {/* Status */}
        <Divider>
          Status
        </Divider>

        <Form.Item
          name="is_active"
          label="Aktiv status"
          valuePropName="checked"
          help="Aktiv kurslar tələbələrə təyin edilə bilər"
        >
          <Switch 
            checkedChildren="Aktiv" 
            unCheckedChildren="Deaktiv"
          />
        </Form.Item>

        {/* Gizli sahələr */}
        <Form.Item name="company" hidden>
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CourseForm;